<template>
  <div class="cc-trial-expiry-screen">
    <v-card
      class="cc-trial-expiry-card pa-6"
      :max-width="showCheckout ? 820 : 540"
      elevation="2"
    >
      <v-card-title
        v-if="!showCheckout"
        class="d-flex align-center"
      >
        <v-icon
          :icon="Icons.CLOCK_ALERT_OUTLINE"
          size="32"
          color="primary"
          class="mr-3"
        />
        <span>{{ t('trial.expiry.headline') }}</span>
      </v-card-title>

      <v-card-text>
        <!-- Admin: can decide how to proceed. Once a decision is recorded the
             screen stays (the customer remains locked) but shows the outcome. -->
        <template v-if="store.isAdmin">
          <!-- T00VOQ5DCZSJ — continuing means concluding the contract right
               here; a direct purchase starts on this page. -->
          <cc-checkout
            v-if="showCheckout"
            data-test="trial-checkout"
          />
          <p
            v-else-if="store.trialDecision === 'cancelled'"
            class="text-body-1 mb-0"
          >
            {{ t('trial.expiry.cancelInfo') }}
          </p>
          <template v-else>
            <p class="text-body-1 mb-4">
              {{ t('trial.expiry.adminSubtitle') }}
            </p>
            <p
              v-if="error"
              class="text-body-2 text-error mb-4"
            >
              {{ t('trial.expiry.error') }}
            </p>
            <div class="d-flex flex-column ga-3">
              <v-btn
                color="primary"
                :disabled="submitting"
                data-test="trial-continue"
                @click="continueToCheckout = true"
              >
                {{ t('trial.expiry.continueButton') }}
              </v-btn>
              <v-btn
                variant="outlined"
                color="primary"
                :disabled="submitting"
                @click="decide('cancelled')"
              >
                {{ t('trial.expiry.cancelButton') }}
              </v-btn>
            </div>
          </template>
        </template>

        <!-- Normal users can only read the notice and log out. -->
        <p
          v-else
          class="text-body-1 mb-0"
        >
          {{ t('trial.expiry.userSubtitle') }}
        </p>
      </v-card-text>

      <v-card-actions class="justify-end">
        <v-btn
          variant="text"
          :prepend-icon="Icons.LOGOUT"
          :disabled="submitting"
          @click="onLogout()"
        >
          {{ t('trial.expiry.logout') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import {computed, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import {Icons} from '@simpleforyou/client-core';
import {useStore} from '@/common/store/store';
import {useAuth} from '@/composables/useAuth';
import {useApiClient} from '@/common/api/useApiClient';
import {TrialApi} from '@/common/api/trial/TrialApi';
import CcCheckout from '@/components/checkout/CcCheckout.vue';

const {t} = useI18n();
const store = useStore();
const {logout} = useAuth();
const apiClient = useApiClient();

const submitting = ref(false);
const error = ref(false);
const continueToCheckout = ref(false);

// A decision to continue recorded before the checkout existed also leads here.
const showCheckout = computed(() => store.isAdmin && store.trialDecision !== 'cancelled'
  && (continueToCheckout.value || store.checkoutRequired || store.trialDecision === 'continue'));

type TrialStatus = {active: boolean; locked: boolean; decision: 'cancelled' | 'continue' | null};

async function decide(decision: 'cancelled'): Promise<void> {
  submitting.value = true;
  error.value = false;
  try {
    const response = await apiClient.post<TrialStatus>(TrialApi.decision(), {decision});
    // The customer stays locked; only the recorded decision changes, which
    // flips the card to the confirmation message.
    store.setTrialLock(response.data?.locked === true, response.data?.decision ?? decision);
  } catch {
    error.value = true;
  } finally {
    submitting.value = false;
  }
}

async function onLogout(): Promise<void> {
  await logout();
}
</script>

<style lang="scss" scoped>
.cc-trial-expiry-screen {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--background, #f5f5f5);
  z-index: 99998;
  padding: 16px;
}

.cc-trial-expiry-card {
  width: 100%;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
}
</style>
